import React, { useEffect, useMemo, useState } from "react";
import { Cookie, Shield, BarChart3, Megaphone, X } from "lucide-react";
import {
  getCookieConsent,
  setCookieConsent,
  onCookieConsentChange,
  OPEN_COOKIE_SETTINGS_EVENT,
} from "../../services/cookieConsent";

/**
 * Cookie consent banner (bottom sheet) + settings dialog.
 *
 * - Shows automatically on first visit (no stored consent yet).
 * - Can be re-opened anytime via `openCookieSettings()` (e.g. from a footer link).
 * - Matches existing modal CI: var(--surface-1), var(--border), var(--accent-700/600),
 *   rounded-2xl, shadow-xl – consistent with InfoModal/ConfirmModal.
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  // Boot: show if no consent stored yet.
  useEffect(() => {
    const existing = getCookieConsent();
    if (!existing) {
      setVisible(true);
      return;
    }
    setPrefs({ analytics: !!existing.analytics, marketing: !!existing.marketing });
  }, []);

  // Listen to external "open settings" events + keep in sync with storage changes.
  useEffect(() => {
    const openHandler = () => {
      const existing = getCookieConsent();
      if (existing) {
        setPrefs({ analytics: !!existing.analytics, marketing: !!existing.marketing });
      }
      setVisible(true);
      setShowSettings(true);
    };
    if (typeof window !== "undefined") {
      window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, openHandler);
    }
    const off = onCookieConsentChange((val) => {
      if (val) {
        setPrefs({ analytics: !!val.analytics, marketing: !!val.marketing });
      }
    });
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, openHandler);
      }
      off();
    };
  }, []);

  // ESC closes the settings dialog (but keeps banner if no choice made yet).
  useEffect(() => {
    if (!showSettings) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setShowSettings(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showSettings]);

  const handleAcceptAll = () => {
    setCookieConsent({ analytics: true, marketing: true });
    setVisible(false);
    setShowSettings(false);
  };
  const handleRejectAll = () => {
    setCookieConsent({ analytics: false, marketing: false });
    setVisible(false);
    setShowSettings(false);
  };
  const handleSavePrefs = () => {
    setCookieConsent(prefs);
    setVisible(false);
    setShowSettings(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Bottom sheet banner */}
      <div
        className="fixed left-0 right-0 bottom-0 z-[70] px-4 pb-[max(16px,env(safe-area-inset-bottom))]"
        role="dialog"
        aria-modal="false"
        aria-label="Cookie settings"
      >
        <div className="mx-auto w-full max-w-3xl rounded-2xl bg-[var(--surface-1)] border border-[color:var(--border)] shadow-2xl overflow-hidden">
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[color:var(--accent-500)]/15 text-[color:var(--accent-500)]">
                <Cookie className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] sm:text-base font-semibold text-[var(--text)]">
                  We use cookies
                </div>
                <p className="mt-1 text-sm text-[var(--text-muted)] leading-relaxed">
                  Strictly necessary cookies are required for the site to work.
                  With your consent we also use cookies for analytics and marketing.
                  You can change your choice any time in the settings.{" "}
                  <a
                    href="/datenschutz"
                    className="underline decoration-dotted text-[var(--text)] hover:text-[color:var(--accent-500)]"
                  >
                    Privacy policy
                  </a>
                </p>
              </div>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                className="order-3 sm:order-1 inline-flex items-center justify-center px-4 h-10 rounded-lg bg-transparent hover:bg-[var(--surface-1-hover)] border border-[color:var(--border)] text-[var(--text)] text-sm font-semibold transition-colors"
              >
                Settings
              </button>
              <button
                type="button"
                onClick={handleRejectAll}
                className="order-2 inline-flex items-center justify-center px-4 h-10 rounded-lg bg-[var(--surface-2)] hover:bg-[var(--surface-1-hover)] text-[var(--text)] text-sm font-semibold transition-colors"
              >
                Necessary only
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                className="order-1 sm:order-3 inline-flex items-center justify-center px-5 h-10 rounded-lg bg-[var(--accent-700)] hover:bg-[var(--accent-600)] text-white text-sm font-semibold transition-colors"
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings dialog */}
      {showSettings ? (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4"
          onClick={() => setShowSettings(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-[var(--surface-1)] border border-[color:var(--border)] shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-[color:var(--border)]">
              <div className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[color:var(--accent-500)]/15 text-[color:var(--accent-500)]">
                <Cookie className="w-4.5 h-4.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold text-[var(--text)]">Cookie settings</div>
                <div className="text-xs text-[var(--text-muted)]">Choose which categories you allow.</div>
              </div>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                aria-label="Close"
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-[var(--surface-1-hover)] text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-3">
              <CategoryRow
                icon={<Shield className="w-4 h-4" />}
                title="Necessary"
                description="Core functionality, login and security. Always on."
                checked
                disabled
              />
              <CategoryRow
                icon={<BarChart3 className="w-4 h-4" />}
                title="Analytics"
                description="Anonymous usage statistics so we can improve the site."
                checked={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              />
              <CategoryRow
                icon={<Megaphone className="w-4 h-4" />}
                title="Marketing"
                description="Personalized content and campaign measurement."
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
              />
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[color:var(--border)]">
              <button
                type="button"
                onClick={handleRejectAll}
                className="inline-flex items-center justify-center px-4 h-10 rounded-lg bg-[var(--surface-2)] hover:bg-[var(--surface-1-hover)] text-[var(--text)] text-sm font-semibold transition-colors"
              >
                Necessary only
              </button>
              <button
                type="button"
                onClick={handleSavePrefs}
                className="inline-flex items-center justify-center px-4 h-10 rounded-lg bg-transparent hover:bg-[var(--surface-1-hover)] border border-[color:var(--border)] text-[var(--text)] text-sm font-semibold transition-colors"
              >
                Save selection
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                className="inline-flex items-center justify-center px-5 h-10 rounded-lg bg-[var(--accent-700)] hover:bg-[var(--accent-600)] text-white text-sm font-semibold transition-colors"
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function CategoryRow({ icon, title, description, checked, onChange, disabled = false }) {
  const id = useMemo(
    () => "cookie-cat-" + Math.random().toString(36).slice(2, 9),
    []
  );
  return (
    <div className="flex items-start gap-3 rounded-xl bg-[var(--surface-2)] border border-[color:var(--border)] p-3">
      <div className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[var(--surface-1)] text-[var(--text-muted)]">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <label htmlFor={id} className="text-[14px] font-semibold text-[var(--text)] select-none cursor-pointer">
          {title}
        </label>
        <div className="text-xs text-[var(--text-muted)] leading-snug">{description}</div>
      </div>
      <Toggle id={id} checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}

function Toggle({ id, checked, onChange, disabled }) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange && onChange(!checked)}
      className={`shrink-0 relative inline-flex items-center h-6 w-10 rounded-full transition-colors ${
        checked ? "bg-[var(--accent-700)]" : "bg-[var(--surface-1)] border border-[color:var(--border)]"
      } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}
